import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import {
  Users,
  BookOpen,
  Calendar,
  User,
  Save,
  RefreshCw,
  AlertCircle,
  ArrowLeft,
} from "lucide-react";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";
import { useNavigate, useParams, useLocation } from "react-router-dom";

const CreateBatchPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEditing = !!id;

  const [courses, setCourses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      batchName: "",
      batchCode: "",
      description: "",
      courseId: "",
      teacherId: "",
      startDate: "",
      endDate: "",
      maxStudents: 50,
      status: "upcoming",
    },
  });

  useEffect(() => {
    fetchCourses();
    fetchTeachers();

    if (isEditing) {
      if (location.state?.batch) {
        populateForm(location.state.batch);
      } else {
        fetchBatchDetails();
      }
    }
  }, [id, isEditing, location.state]);

  const populateForm = (batch) => {
    setValue("batchName", batch.batchName);
    setValue("batchCode", batch.batchCode);
    setValue("description", batch.description);
    setValue("courseId", batch.courseId);
    setValue("teacherId", batch.teacherId);
    setValue(
      "startDate",
      batch.startDate
        ? new Date(batch.startDate).toISOString().split("T")[0]
        : ""
    );
    setValue(
      "endDate",
      batch.endDate ? new Date(batch.endDate).toISOString().split("T")[0] : ""
    );
    setValue("maxStudents", batch.maxStudents);
    setValue("status", batch.status);
  };

  const fetchBatchDetails = async () => {
    try {
      const { data } = await axios.get(`${server}/batch/all`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (data.success && data.data) {
        const batch = data.data.find((b) => b.id == id);
        if (batch) {
          populateForm(batch);
        } else {
          toast.error("Batch not found");
          navigate("/my/batches");
        }
      }
    } catch (error) {
      toast.error("Failed to fetch batch details");
      navigate("/my/batches");
    }
  };

  const fetchCourses = async () => {
    try {
      const { data } = await axios.get(`${server}/course/all`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (data.success && data.data) {
        setCourses(data.data);
      }
    } catch (error) {
      toast.error("Failed to fetch courses");
    }
  };

  const fetchTeachers = async () => {
    try {
      const { data } = await axios.get(`${server}/teacher/all`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (data.success && data.data) {
        setTeachers(data.data);
      }
    } catch (error) {
      toast.error("Failed to fetch teachers");
    }
  };

  const onSubmit = async (data) => {
    try {
      const payload = {
        ...data,
        courseId: Number(data.courseId),
        teacherId: Number(data.teacherId),
        maxStudents: Number(data.maxStudents),
        startDate: new Date(data.startDate).toISOString(),
        endDate: new Date(data.endDate).toISOString(),
      };

      if (isEditing) {
        await axios.put(`${server}/batch/updateById`, payload, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: { batchId: id },
        });
        toast.success("Batch updated successfully!");
      } else {
        await axios.post(`${server}/batch/new`, payload, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        toast.success("Batch created successfully!");
      }

      navigate("/my/batches");
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          (isEditing ? "Failed to update batch" : "Failed to create batch")
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6">
      <motion.div
        className="max-w-4xl mx-auto space-y-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/my/batches")}
            className="p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors border border-gray-700"
          >
            <ArrowLeft className="w-5 h-5 text-gray-300" />
          </button>
          <div>
            <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl">
                <Users className="w-8 h-8 text-white" />
              </div>
              {isEditing ? "Edit Batch" : "Create New Batch"}
            </h1>
            <p className="text-gray-400 text-lg">
              {isEditing
                ? "Update batch details"
                : "Set up a new batch for your course"}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-400" />
              Basic Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Batch Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Batch Name *
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="e.g., JEE Main 2024 - Batch A"
                  {...register("batchName", {
                    required: "Batch name is required",
                  })}
                />
                {errors.batchName && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.batchName.message}
                  </p>
                )}
              </div>

              {/* Batch Code */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Batch Code *
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="e.g., JEE-A-24"
                  {...register("batchCode", {
                    required: "Batch code is required",
                  })}
                />
                {errors.batchCode && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.batchCode.message}
                  </p>
                )}
              </div>

              {/* Max Students */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Maximum Students *
                </label>
                <input
                  type="number"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="50"
                  {...register("maxStudents", {
                    required: "Max students is required",
                    min: { value: 1, message: "Must be at least 1" },
                  })}
                />
                {errors.maxStudents && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.maxStudents.message}
                  </p>
                )}
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  rows="3"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors resize-none"
                  placeholder="Brief description of the batch..."
                  {...register("description")}
                />
              </div>
            </div>
          </div>

          {/* Course & Teacher */}
          <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-purple-400" />
              Course & Teacher Assignment
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Course Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Course *
                </label>
                <select
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white focus:outline-none focus:border-blue-500 transition-colors"
                  {...register("courseId", {
                    required: "Please select a course",
                  })}
                >
                  <option value="">Select Course</option>
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.courseName}
                    </option>
                  ))}
                </select>
                {errors.courseId && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.courseId.message}
                  </p>
                )}
              </div>

              {/* Teacher Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Teacher
                </label>
                <select
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white focus:outline-none focus:border-blue-500 transition-colors"
                  {...register("teacherId")}
                >
                  <option value="">Select Teacher</option>
                  {teachers.map((teacher) => (
                    <option key={teacher.id} value={teacher.id}>
                      {teacher.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Schedule */}
          <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-green-400" />
              Schedule
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Start Date */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Start Date *
                </label>
                <input
                  type="date"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white focus:outline-none focus:border-blue-500 transition-colors"
                  {...register("startDate", {
                    required: "Start date is required",
                  })}
                />
                {errors.startDate && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.startDate.message}
                  </p>
                )}
              </div>

              {/* End Date */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  End Date *
                </label>
                <input
                  type="date"
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white focus:outline-none focus:border-blue-500 transition-colors"
                  {...register("endDate", {
                    required: "End date is required",
                  })}
                />
                {errors.endDate && (
                  <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.endDate.message}
                  </p>
                )}
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Status *
                </label>
                <select
                  className="w-full px-4 py-3 bg-gray-900 border-2 border-gray-700 rounded-xl text-white focus:outline-none focus:border-blue-500 transition-colors"
                  {...register("status")}
                >
                  <option value="upcoming">Upcoming</option>
                  <option value="ongoing">Ongoing</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 justify-end">
            <button
              type="button"
              onClick={() => navigate("/my/batches")}
              className="px-8 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors border border-gray-600"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => reset()}
              className="flex items-center gap-2 px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors border border-gray-600"
            >
              <RefreshCw className="w-5 h-5" />
              Reset
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2 px-8 py-3 bg-gradient-to-br from-blue-600 to-cyan-600 text-white rounded-lg hover:shadow-lg hover:shadow-blue-500/20 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  {isEditing ? "Updating..." : "Creating..."}
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  {isEditing ? "Update Batch" : "Create Batch"}
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default CreateBatchPage;
